import { getStoreKey } from '../config/backend';
// ============================================================
// mailWorkflows — メール種別ごとの標準手順（2026-09-22）
//
// 正: booking-platform/md/ai-control-center.md §16-6y
//
// 8/16〜9/21 の予約セッション35本と処理済み decision 100件から抽出した
// 「メールの型 → 手順」の台帳。エージェントはこの手順に沿って対話する:
//   - 予約セッションをメールから開いたとき、種別を判定して手順を文脈に注入する
//   - 入力欄の上に手順のチップを出し、オペレータはタップで次の一手を指示できる
//   - list_reservation_mails の各行にも type を付ける（メール起点でない場合の判定材料）
// 判定は**件名と差出人**で決定的に行う（AI に分類を任せない = 構造で縛る）。
// ============================================================

export interface WorkflowStep {
  /** チップの表示名 */
  label: string;
  /** チップを押したときにエージェントへ送る指示文（そのまま送信） */
  instruction: string;
  /**
   * 手順カードを先に出すチップか（2026-10-30 前田さん「チップにも手順カードを」）。
   * データの変更とメールの起案が合わせて2つ以上あるもの、またはスタッフの操作（Square など）が後に続くものを true にする。
   * 読み取りだけ・メールの起案だけ・アーカイブだけのチップは false（今までどおりすぐ実行）
   */
  plan?: boolean;
}

/** plan のチップの指示文の末尾に付ける（AI がまず propose_plan を呼ぶように。taskPlan.planGuard でも縛る） */
export const PLAN_CHIP_SUFFIX = '（まず手順カードで完了までの手順を示し、承認を受けてから1つずつ進めてください）';

export interface MailWorkflow {
  key: string;
  label: string;
  /** 手順の説明（システムプロンプトに注入する。番号付きで書く） */
  procedure: string[];
  /** オペレータ向けチップ（順番どおり。分岐があるものは並べて出す） */
  steps: WorkflowStep[];
}

const OWN_DOMAIN = /@(n-car\.jp|n-car\.info|trailernchu\.co\.jp|pit-rent\.com)/i;

export const MAIL_WORKFLOWS: Record<string, MailWorkflow> = {
  lp_request: {
    key: 'lp_request',
    label: 'LP リクエスト予約（承諾待ち）',
    procedure: [
      '在庫を確認する（list_available_vehicles。満車なら同系統の上位版への無料アップグレード可否も見る）',
      '在庫あり → ステータスを offer にし、テンプレ「リクエスト OK」で承諾メールを起案（貸出/返却が深夜早朝帯 20:00〜8:59 なら「リクエスト（深夜早朝）OK」。自由作文しない・アップグレードには触れない）',
      '在庫ありで即確定する場合（スタッフが「そのまま確定」と言ったとき）→ オファーを経ずに booked にし、テンプレ「予約確定（在庫ありで即確定）」で確定のご連絡。返信待ちのフォローアップは設定しない',
      '在庫なし → 代替車種の提案（テンプレ「リクエスト offer」）か、満車のお断り（「リクエスト full」）',
      '通知メールに set_mail_followup（既定 翌日12:00・返信なければ予約に至らなかったへ）',
      '通知メールをアーカイブ。booked にするのは**お客様の返信が来てから**',
    ],
    steps: [
      { label: '在庫を確認', instruction: 'この予約の期間で在庫を確認してください。満車なら同系統の上位版への無料アップグレードができるかも見てください。' },
      { label: 'リクエストOK（承諾）', instruction: 'リクエストを承諾します。ステータスをオファー中にして、テンプレ「リクエスト OK」で承諾メールを起案してください（貸出か返却が深夜早朝帯 20:00〜8:59 なら「リクエスト（深夜早朝）OK」）。アップグレードしている場合も本文には書かないでください。', plan: true },
      { label: '即確定（オファーなし）', instruction: '在庫があるので、オファーを経ずにこの予約をそのまま予約確定（booked）にしてください。確定のご連絡はテンプレ「予約確定（在庫ありで即確定）」で起案し、返信待ちのフォローアップは設定せずに通知メールをアーカイブしてください。', plan: true },
      { label: '代替を提案', instruction: '希望車種は満車なので、代替車種を提案するメールをテンプレ「リクエスト offer」で起案してください。' },
      { label: '満車でお断り', instruction: '満車のためお断りするメールをテンプレ「リクエスト full」で起案してください。' },
      { label: 'フォローアップ＋アーカイブ', instruction: 'この通知メールに翌日12:00のフォローアップを設定し（返信なければ予約に至らなかったへ）、メールをアーカイブしてください。' },
    ],
  },
  request_reply: {
    key: 'request_reply',
    label: 'リクエスト承諾へのお客様返信',
    procedure: [
      '返信内容を読む（予約でお願いします / 変更希望 / 辞退 のどれか）',
      '「お願いします」→ ステータスを booked にし、予約確定メールをスレッド返信で起案（アップグレードには触れない）',
      '変更希望 → 内容に応じて日時・車種を確認してから返信',
      '辞退 → ステータスを nonreserved にし、お礼の返信',
      '返信メールをアーカイブ（フォローアップは返信を検知して自動解消される）',
    ],
    steps: [
      { label: '返信を確認', instruction: 'お客様からの返信を読んで、予約の意思（お願いします／変更希望／辞退）を要約してください。' },
      { label: '予約確定＋確定メール', instruction: '予約OKなのでステータスを予約確定に変更し、お客様の返信へのスレッド返信として予約確定メールを起案してください。アップグレードについては書かないでください。', plan: true },
      { label: '辞退→予約に至らなかった', instruction: 'お客様が辞退されたので、ステータスを「予約に至らなかった」に変更し、お礼の返信を起案してください。', plan: true },
      { label: 'アーカイブ', instruction: 'このメールをアーカイブしてください。' },
    ],
  },
  lp_confirmed: {
    key: 'lp_confirmed',
    label: 'LP 確定予約の通知',
    procedure: [
      '通知の備考・コメント欄に要望（チャイルドシート・送迎・ルーフボックス等）があるか確認する',
      '要望あり → 承知した旨の案内メールを起案（返信期限の文言は入れない）',
      '要望なし → 対応不要。通知をアーカイブ',
    ],
    steps: [
      { label: '要望を確認', instruction: 'この予約通知の備考・コメント欄にお客様の要望があるか確認して、あれば要約してください。' },
      { label: '承知メールを起案', instruction: 'お客様の要望を承知した旨の案内メールを起案してください。返信期限の文言は不要です。' },
      { label: 'アーカイブ', instruction: 'この通知メールをアーカイブしてください。' },
    ],
  },
  insurance_change: {
    key: 'insurance_change',
    label: '補償変更の依頼（NOC/CDW）',
    procedure: [
      'apply_price_change で NOC/CDW のフラグと金額をまとめて再計算→反映（確認カードで金額と計算根拠を確認して承認）',
      '請求書: 未払いなら取消→再作成 / 支払い済みなら差額のみの請求書（追加ルール#9。Square は人が操作）',
      '（必要なら）お客様へ追加料金のご案内メールを起案',
      '依頼メールをアーカイブ',
    ],
    steps: [
      { label: '補償と金額を反映', instruction: 'お客様の希望どおり NOC/CDW を付けて、金額も apply_price_change で再計算して反映してください。', plan: true },
      { label: '追加料金の案内メール', instruction: '追加になった補償と金額をお客様にご案内するメールを起案してください。' },
      { label: 'アーカイブ', instruction: 'この依頼メールをアーカイブしてください。' },
    ],
  },
  prepay_reply: {
    key: 'prepay_reply',
    label: '事前決済の御礼へのお客様返信（免許証など）',
    procedure: [
      '添付があれば check_mail_attachments で免許証を判定（氏名一致・有効期限）',
      '問題なければ set_reservation_checks で免許確認をオンにする（確認カード）',
      'ご提示のお礼を返信（スレッド返信）',
      'メールをアーカイブ',
    ],
    steps: [
      { label: '免許証を判定', instruction: 'このメールの添付の免許証を確認して、予約者名と一致するか・有効期限に問題がないか教えてください。' },
      { label: '免許確認をON', instruction: '免許証を確認できたので、予約の免許確認チェックをオンにしてください。' },
      { label: 'お礼を返信', instruction: '免許証のご提示へのお礼をスレッド返信で起案してください。' },
      { label: 'アーカイブ', instruction: 'このメールをアーカイブしてください。' },
    ],
  },
  confirmed_reply: {
    key: 'confirmed_reply',
    label: '確定予約へのお客様返信（内容で分岐）',
    procedure: [
      '本文を読んで用件を分類する: 日時変更 / キャンセル / 質問・要望 / 到着連絡',
      '日時変更 → check_schedule_conflict → preview_price_change → update_reservation → 承り返信',
      'キャンセル → cancel_reservation_full（証明書付きの無償キャンセルは check_mail_attachments で確認）→ 承り返信',
      '質問・要望 → 送迎はプランで判定（セルフはセルフプランの案内）。回答をスレッド返信',
      '返信後にメールをアーカイブ',
    ],
    steps: [
      { label: '用件を確認', instruction: 'お客様の返信を読んで用件（日時変更／キャンセル／質問・要望／到着連絡）を要約してください。' },
      { label: '日時変更を確認', instruction: 'お客様の希望日時に変更した場合、車両の重なりと料金への影響を確認してください。' },
      { label: 'キャンセル処理', instruction: 'お客様からのキャンセル依頼なので、キャンセル一式（ステータス・請求書・メモ）を実行して、承りのメールを起案してください。', plan: true },
      { label: '返信を起案', instruction: 'お客様の質問・要望に対する返信をスレッド返信で起案してください。' },
      { label: 'アーカイブ', instruction: 'このメールをアーカイブしてください。' },
    ],
  },
  invoice_paid: {
    key: 'invoice_paid',
    label: '請求書の支払い完了',
    procedure: [
      'set_reservation_checks で支払い確認をオンにする',
      '事前決済の御礼メール（免許証アップロードと事前受付の依頼を含む）を起案',
      'メールをアーカイブ',
    ],
    steps: [
      { label: '支払い確認をON', instruction: '支払いが完了したので、予約の支払い確認チェックをオンにしてください。' },
      { label: '御礼メールを起案', instruction: '事前決済の御礼メール（免許証のアップロードと事前受付のお願いを含む）を起案してください。' },
      { label: 'アーカイブ', instruction: 'このメールをアーカイブしてください。' },
    ],
  },
};

/**
 * 件名・差出人からメール種別を決定的に判定する。判定できなければ null。
 * ⚠ 本文の意味解釈はしない（それは AI の仕事）。ここは「型」を当てるだけ。
 */
/** 各国メーラーの返信・転送接頭辞（Re / AW=独 / Odp=波 / SV=北欧 / Réf=仏 / 回复=中 など） */
const REPLY_PREFIX = /^\s*(Re|Fwd?|FW|AW|WG|Odp|SV|VS|R[ée]f|Rif|Antw|回复|答复|转发|回覆|轉寄)\s*[:：]/i;

export function classifyMail(subject: string, sender: string): string | null {
  const s = String(subject || '');
  const own = OWN_DOMAIN.test(String(sender || ''));
  if (own) {
    if (/補償変更の依頼/.test(s)) return 'insurance_change';
    if (/【予約受付】/.test(s)) return /リクエスト/.test(s) ? 'lp_request' : 'lp_confirmed';
    return null;   // 自社の他の通知（自動返信・Square 等）は型なし
  }
  if (/請求書の支払いが完了/.test(s)) return 'invoice_paid';
  // 英語版テンプレ「Thank you for your reservation request」も同じ型（2026-09-26 記録#33:
  // ポーランドのお客様の「Odp: …reservation request」が型なしになった）
  if (/リクエスト予約ありがとうございます|reservation request/i.test(s)) return 'request_reply';
  if (/事前決済の御礼/.test(s)) return 'prepay_reply';
  if (REPLY_PREFIX.test(s) || /Reservation|Confirmation|ご予約/i.test(s)) return 'confirmed_reply';
  return null;
}

/** 那覇だけの運用の書き添え（宮古の画面では消す。2026-10-03 宮古の点検） */
const NAHA_ONLY_NOTES = [
  '（貸出か返却が深夜早朝帯 20:00〜8:59 なら「リクエスト（深夜早朝）OK」）',
  '（貸出/返却が深夜早朝帯 20:00〜8:59 なら「リクエスト（深夜早朝）OK」。',
];
function forStore(wf: MailWorkflow): MailWorkflow {
  if (getStoreKey() !== 'pit') return wf;
  const strip = (t: string) => NAHA_ONLY_NOTES.reduce((acc, n) => acc.split(n).join(n.endsWith('。') ? '（' : ''), t);
  return { ...wf, procedure: wf.procedure.map(strip), steps: wf.steps.map((st) => ({ ...st, instruction: strip(st.instruction) })) };
}

/** チップが実際に送る指示文（plan のチップは手順カードのお願いを付ける） */
export function chipInstruction(st: WorkflowStep): string {
  return st.plan ? st.instruction + PLAN_CHIP_SUFFIX : st.instruction;
}

export function getWorkflow(key: string | null | undefined): MailWorkflow | null {
  const wf = key ? MAIL_WORKFLOWS[key] ?? null : null;
  if (!wf) return null;
  const local = forStore(wf);
  return { ...local, steps: local.steps.map((st) => ({ ...st, instruction: chipInstruction(st) })) };
}

/** 送られた文がチップの指示文なら、そのチップを返す（店舗の書き換え・手順カードのお願いの有無どちらでも当てる） */
export function findChipStep(text: string): WorkflowStep | null {
  const t = String(text ?? '').trim();
  if (!t) return null;
  for (const key of Object.keys(MAIL_WORKFLOWS)) {
    const raw = MAIL_WORKFLOWS[key];
    const local = forStore(raw);
    for (let i = 0; i < raw.steps.length; i++) {
      const cands = [raw.steps[i].instruction, local.steps[i].instruction];
      if (cands.some((c) => t === c || t === c + PLAN_CHIP_SUFFIX)) return raw.steps[i];
    }
  }
  return null;
}

/** システムプロンプトに注入する手順ブロック */
export function buildWorkflowPrompt(key: string | null | undefined): string {
  const wf = getWorkflow(key);
  if (!wf) return '';
  return [
    `# このメールの標準手順: ${wf.label}`,
    '',
    '次の手順で進める。各段階の実行前に、その段階で何をするかを一言で示してから道具を使う。',
    ...wf.procedure.map((p, i) => `${i + 1}. ${p}`),
    '',
    '手順にない依頼が来たらそれに従ってよいが、飛ばした段階があれば最後に「未実施: 〜」と伝える。',
  ].join('\n');
}
